/**
 * Implementation of loadBidDetails.
 * Extracted for use by template core hooks to reduce file size.
 * Helpers (loadContractsAndHandlingTypes, loadSuppliersForBid, loadNdcOptions) stay in Full and are passed in deps.
 */
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import { mapBackendToFrontendFields, parseDateString } from '../bidAnalysisTemplate.logic';
import type { LoadBidDetailsDeps } from './loadBidDetailsImpl.types';

export async function loadBidDetailsImpl(deps: LoadBidDetailsDeps): Promise<void> {
  const {
    bidId,
    setLoading,
    setBidStatus,
    setSelectedStatus,
    setBidOwner,
    gsmsHndlgTypeManuallyChangedRef,
    setGsmsHndlgType,
    setFormData,
    setTemplateData,
    setYearFormData,
    yearFormDataRef,
    setPackSizeFormData,
    setSelectedConcept,
    setActivePackSizeTab,
    setActiveYearTab,
    setAnalysisType,
    setPricingValidatedChecked,
    hasLoadedInitialData,
    setChartDateRange,
    setCalendarViewDates,
    setNdcValidated,
    BidAnalysisStore,
    supplierName,
    selectedConcept,
    loadContractsAndHandlingTypes,
    loadSuppliersForBid,
    loadNdcOptions,
  } = deps;

  if (!bidId) return;

  setLoading(true);
  try {
    await loadContractsAndHandlingTypes();
    const bidIdNum = parseInt(bidId);
    const existing = BidAnalysisStore.currentBidDetails;
    const alreadyLoaded = existing && (String((existing as any).id) === String(bidId) || String((existing as any).bid_id) === String(bidId));
    if (!alreadyLoaded) {
      await BidAnalysisStore.fetchBidDetails(bidIdNum);
    }
    const bidDetails = BidAnalysisStore.currentBidDetails;

    if (!bidDetails) {
      console.error('No bid details returned');
      setLoading(false);
      return;
    }

    if (bidDetails.status) {
      setBidStatus(bidDetails.status);
      setSelectedStatus(bidDetails.status);
    }

    const owner =
      bidDetails.analyst ||
      bidDetails.created_by ||
      bidDetails.owner ||
      bidDetails.user ||
      bidDetails.createdBy ||
      bidDetails.created_by_name ||
      bidDetails.creator ||
      null;
    setBidOwner(owner);

    if (bidDetails.gsms_handling_type) {
      gsmsHndlgTypeManuallyChangedRef.current = false;
      setGsmsHndlgType(bidDetails.gsms_handling_type);
    }

    const apiProductType = bidDetails.product_type || 'direct';
    const apiTemplateType = bidDetails.template_type || 'mas';

    if (apiTemplateType === 'nc') {
      const contractsValue =
        (bidDetails as any).contract_type || bidDetails.intended_contracts || '';
      if (contractsValue) {
        setFormData((prev: any) => ({ ...prev, contracts: contractsValue }));
      }
    }

    const templateStructure = await bidAnalysisServices.getTemplateData(apiProductType);
    setTemplateData(templateStructure);

    const formDetails = bidDetails.form_details || { form_data: [] };
    const isNC = apiTemplateType === 'nc';

    const packSizeData: Record<number, any> = {};
    const yearFormDataByTab: Record<number, Record<number, any>> = {};

    if (!formDetails.form_data || !Array.isArray(formDetails.form_data)) {
      console.warn('[loadBidDetails] form_data is not an array or is missing:', formDetails.form_data);
    }

    formDetails.form_data.forEach((concept: any, conceptIndex: number) => {
      const packSizes = Object.keys(concept.pack_size || {});

      if (packSizes.length > 0) {
        packSizes.forEach((packSize, packIndex) => {
          const tabIndex = conceptIndex * 1000 + packIndex;

          if (isNC) {
            const packDataArray = concept.pack_size[packSize];
            const yearDataMap: Record<number, any> = {
              0: {},
              1: {},
              2: {},
              3: {},
              4: {},
            };

            if (Array.isArray(packDataArray)) {
              packDataArray.forEach((yearEntry: any) => {
                const optionYear = yearEntry.option_year;
                if (optionYear >= 1 && optionYear <= 5) {
                  const yearIndex = optionYear - 1;
                  if (yearIndex >= 0 && yearIndex <= 4) {
                    const mappedData = mapBackendToFrontendFields(yearEntry);
                    const {
                      vaTmsPercent: _vaTmsPercent,
                      dodTmsPercent: _dodTmsPercent,
                      totalTmsPercent: _totalTmsPercent,
                      targetAnnualUsage: _targetAnnualUsage,
                      ...ncMappedData
                    } = mappedData;

                    const processedData: any = {};
                    const numericFields = [
                      'vaAnnualNcEst',
                      'dodAnnualNcEst',
                      'ihsAnnualNcEst',
                      'bopAnnualNcEst',
                      'ncEstAnnualUsage',
                      'suggestedBidPrice',
                      'gsmsCostFromMfg',
                      'gsmsNetCostFromMfg',
                      'gsmsCost',
                      'gsmsProfit',
                      'wac',
                      'gsmsIffFee',
                      'gsmsMarketingFee',
                      'contractAnnualRevenue',
                      'contractAnnualGP',
                      'contractAnnualCost',
                      'govSavingsAnalysis',
                      'modeWac',
                      'lowWac',
                      'topRxPrice',
                      'costPlusPrice',
                      'nadacPrice',
                      'gsmsBottleCost',
                      'gsmsCapCost',
                      'gsmsLabelCost',
                      'gsmsInsertCost',
                      'gsmsBoxCartonCost',
                      'gsmsDesiccantCost',
                      'outsourceRepackFee',
                      'blendedWhlsFee',
                      'gsmsFrtCost',
                      'pv2Percent',
                      'gsmsLabor',
                      'supplier_comparable_wac',
                    ];
                    Object.keys(ncMappedData).forEach(key => {
                      const value = ncMappedData[key];
                      if (numericFields.includes(key)) {
                        if (value === null || value === undefined) {
                          const inputNumberKeys = [
                            'suggestedBidPrice',
                            'wac',
                            'gsmsProfit',
                            'modeWac',
                            'lowWac',
                            'topRxPrice',
                            'costPlusPrice',
                            'nadacPrice',
                          ];
                          processedData[key] = inputNumberKeys.includes(key) ? null : '';
                        } else if (typeof value === 'number') {
                          const inputNumberKeys = [
                            'suggestedBidPrice',
                            'wac',
                            'gsmsProfit',
                            'modeWac',
                            'lowWac',
                            'topRxPrice',
                            'costPlusPrice',
                            'nadacPrice',
                          ];
                          processedData[key] = inputNumberKeys.includes(key)
                            ? value
                            : value === 0
                              ? '0'
                              : value.toString();
                        } else {
                          processedData[key] = value;
                        }
                      } else {
                        processedData[key] = value;
                      }
                    });

                    const hasGsmsProfit =
                      (processedData.gsmsProfit !== null &&
                        processedData.gsmsProfit !== undefined &&
                        processedData.gsmsProfit !== '' &&
                        processedData.gsmsProfit !== '0.00' &&
                        processedData.gsmsProfit !== 0) ||
                      (yearEntry.gsms_profit !== null &&
                        yearEntry.gsms_profit !== undefined &&
                        yearEntry.gsms_profit !== '' &&
                        yearEntry.gsms_profit !== '0.00' &&
                        yearEntry.gsms_profit !== 0);

                    const hasGsmsCost =
                      (processedData.gsmsCost !== null &&
                        processedData.gsmsCost !== undefined &&
                        processedData.gsmsCost !== '' &&
                        processedData.gsmsCost !== '0.00' &&
                        processedData.gsmsCost !== 0) ||
                      (yearEntry.gsms_cost !== null &&
                        yearEntry.gsms_cost !== undefined &&
                        yearEntry.gsms_cost !== '' &&
                        yearEntry.gsms_cost !== '0.00' &&
                        yearEntry.gsms_cost !== 0);

                    yearDataMap[yearIndex] = {
                      ...yearDataMap[yearIndex],
                      ...processedData,
                      conceptName: concept.concept_name || '',
                      packSize: packSize,
                      selectedMfgNdc:
                        mappedData.selectedMfgNdc ||
                        mappedData.mfg_ndc ||
                        mappedData.gsms_mfg_ndc ||
                        null,
                      mfg_ndc: mappedData.mfg_ndc || mappedData.gsms_mfg_ndc || null,
                      gsms_mfg_ndc: mappedData.gsms_mfg_ndc || null,
                      gsms_ndc: mappedData.gsms_ndc || null,
                      usagePeriodStartDate: mappedData.usagePeriodStartDate || null,
                      usagePeriodEndDate: mappedData.usagePeriodEndDate || null,
                      modeWac: mappedData.modeWac !== undefined ? mappedData.modeWac : null,
                      lowWac: mappedData.lowWac !== undefined ? mappedData.lowWac : null,
                      topRxPrice:
                        mappedData.topRxPrice !== undefined ? mappedData.topRxPrice : null,
                      topRxVendor:
                        mappedData.topRxVendor !== undefined ? mappedData.topRxVendor : null,
                      costPlusPrice:
                        mappedData.costPlusPrice !== undefined ? mappedData.costPlusPrice : null,
                      nadacPrice:
                        mappedData.nadacPrice !== undefined ? mappedData.nadacPrice : null,
                      _gsmsProfitManuallySet: hasGsmsProfit,
                      _gsmsCostFromApi: hasGsmsCost,
                    };
                  }
                }
              });
            }

            let sharedNdcData: any = {};
            if (Array.isArray(packDataArray) && packDataArray.length > 0) {
              const yearWithNdc = packDataArray.find(
                (entry: any) => entry.gsms_mfg_ndc || entry.mfg_ndc || entry.gsms_ndc
              );
              if (yearWithNdc) {
                const mappedNdcData = mapBackendToFrontendFields(yearWithNdc);
                sharedNdcData = {
                  selectedMfgNdc:
                    mappedNdcData.selectedMfgNdc ||
                    mappedNdcData.mfg_ndc ||
                    mappedNdcData.gsms_mfg_ndc ||
                    null,
                  mfg_ndc: mappedNdcData.mfg_ndc || mappedNdcData.gsms_mfg_ndc || null,
                  gsms_mfg_ndc: mappedNdcData.gsms_mfg_ndc || null,
                  gsms_ndc: mappedNdcData.gsms_ndc || null,
                };
                Object.keys(yearDataMap).forEach(yearIndexStr => {
                  const yearIndex = parseInt(yearIndexStr, 10);
                  if (!isNaN(yearIndex)) {
                    yearDataMap[yearIndex] = {
                      ...yearDataMap[yearIndex],
                      ...sharedNdcData,
                    };
                  }
                });
              }
            }

            yearFormDataByTab[tabIndex] = yearDataMap;

            const baseYearEntry = Array.isArray(packDataArray)
              ? packDataArray.find((entry: any) => entry.option_year === 1)
              : null;
            const packData = baseYearEntry || {};
            const mappedData = mapBackendToFrontendFields(packData);

            if (Object.keys(sharedNdcData).length > 0) {
              Object.assign(mappedData, sharedNdcData);
            }

            const processedMappedData: any = {};
            const numericFieldsPack = [
              'vaAnnualNcEst',
              'dodAnnualNcEst',
              'ihsAnnualNcEst',
              'bopAnnualNcEst',
              'ncEstAnnualUsage',
              'suggestedBidPrice',
              'gsmsCostFromMfg',
              'gsmsNetCostFromMfg',
              'gsmsCost',
              'gsmsProfit',
              'wac',
              'gsmsIffFee',
              'gsmsMarketingFee',
              'contractAnnualRevenue',
              'contractAnnualGP',
              'contractAnnualCost',
              'govSavingsAnalysis',
              'modeWac',
              'lowWac',
              'topRxPrice',
              'costPlusPrice',
              'nadacPrice',
              'gsmsBottleCost',
              'gsmsCapCost',
              'gsmsLabelCost',
              'gsmsInsertCost',
              'gsmsBoxCartonCost',
              'gsmsDesiccantCost',
              'outsourceRepackFee',
              'blendedWhlsFee',
              'gsmsFrtCost',
              'pv2Percent',
              'gsmsLabor',
              'supplier_comparable_wac',
            ];
            Object.keys(mappedData).forEach(key => {
              const value = mappedData[key];
              if (numericFieldsPack.includes(key)) {
                if (value === null || value === undefined) {
                  const inputNumberKeys = [
                    'suggestedBidPrice',
                    'wac',
                    'gsmsProfit',
                    'modeWac',
                    'lowWac',
                    'topRxPrice',
                    'costPlusPrice',
                    'nadacPrice',
                  ];
                  processedMappedData[key] = inputNumberKeys.includes(key) ? null : '';
                } else if (typeof value === 'number') {
                  const inputNumberKeys = [
                    'suggestedBidPrice',
                    'wac',
                    'gsmsProfit',
                    'modeWac',
                    'lowWac',
                    'topRxPrice',
                    'costPlusPrice',
                    'nadacPrice',
                  ];
                  processedMappedData[key] = inputNumberKeys.includes(key)
                    ? value
                    : value === 0
                      ? '0'
                      : value.toString();
                } else {
                  processedMappedData[key] = value;
                }
              } else {
                processedMappedData[key] = value;
              }
            });

            const ncDefaults = {
              conceptName: concept.concept_name || '',
              packSize: packSize,
              vaAnnualNcEst: '0',
              dodAnnualNcEst: '0',
              ihsAnnualNcEst: '0',
              bopAnnualNcEst: '0',
              ncEstAnnualUsage: '0',
              suggestedBidPrice: 0,
              wac: 0,
              gsmsCostFromMfg: '',
              gsmsNetCostFromMfg: '0.00',
              gsmsIffFee: '0.00',
              gsmsMarketingFee: '0.00',
              contractAnnualRevenue: '0.00',
              gsmsProfit: 0,
              contractAnnualGP: '0.00',
              contractAnnualCost: '0.00',
              govSavingsAnalysis: '0.00',
              approvedBy: '',
            };

            const hasGsmsProfitPack =
              (processedMappedData.gsmsProfit !== null &&
                processedMappedData.gsmsProfit !== undefined &&
                processedMappedData.gsmsProfit !== '' &&
                processedMappedData.gsmsProfit !== '0.00' &&
                processedMappedData.gsmsProfit !== 0) ||
              (packData.gsms_profit !== null &&
                packData.gsms_profit !== undefined &&
                packData.gsms_profit !== '' &&
                packData.gsms_profit !== '0.00' &&
                packData.gsms_profit !== 0);

            const hasGsmsCostPack =
              (processedMappedData.gsmsCost !== null &&
                processedMappedData.gsmsCost !== undefined &&
                processedMappedData.gsmsCost !== '' &&
                processedMappedData.gsmsCost !== '0.00' &&
                processedMappedData.gsmsCost !== 0) ||
              (packData.gsms_cost !== null &&
                packData.gsms_cost !== undefined &&
                packData.gsms_cost !== '' &&
                packData.gsms_cost !== '0.00' &&
                packData.gsms_cost !== 0);

            packSizeData[tabIndex] = {
              ...ncDefaults,
              ...processedMappedData,
              selectedMfgNdc:
                mappedData.selectedMfgNdc ||
                mappedData.mfg_ndc ||
                mappedData.gsms_mfg_ndc ||
                null,
              mfg_ndc: mappedData.mfg_ndc || mappedData.gsms_mfg_ndc || null,
              gsms_mfg_ndc: mappedData.gsms_mfg_ndc || null,
              gsms_ndc: mappedData.gsms_ndc || null,
              modeWac: mappedData.modeWac !== undefined ? mappedData.modeWac : null,
              lowWac: mappedData.lowWac !== undefined ? mappedData.lowWac : null,
              topRxPrice: mappedData.topRxPrice !== undefined ? mappedData.topRxPrice : null,
              topRxVendor: mappedData.topRxVendor !== undefined ? mappedData.topRxVendor : null,
              costPlusPrice:
                mappedData.costPlusPrice !== undefined ? mappedData.costPlusPrice : null,
              nadacPrice: mappedData.nadacPrice !== undefined ? mappedData.nadacPrice : null,
              _gsmsProfitManuallySet: hasGsmsProfitPack,
              _gsmsCostFromApi: hasGsmsCostPack,
              vaTmsPercent: undefined,
              dodTmsPercent: undefined,
              totalTmsPercent: undefined,
              targetAnnualUsage: undefined,
            };

            Object.keys(packSizeData[tabIndex]).forEach(key => {
              if (packSizeData[tabIndex][key] === undefined) {
                delete packSizeData[tabIndex][key];
              }
            });
          } else {
            const packData = concept.pack_size[packSize] || {};
            const mappedData = mapBackendToFrontendFields(packData);
            const defaults = {
              conceptName: concept.concept_name || '',
              packSize: packSize,
              vaAnnualUsage: '0',
              dodAnnualUsage: '0',
              vaTmsPercent: '0.00',
              dodTmsPercent: '0.00',
              suggestedBidPrice: '0.00',
              wac: '0.00',
              gsmsCostFromMfg: '',
              gsmsNetCostFromMfg: '0.00',
              gsmsHndlgType:
                bidDetails.gsms_handling_type ||
                (apiProductType === 'indirect' ? 'Marketing Income' : ''),
              mfgBottleSize: packSize,
              gsmsCost: '0.00',
              gsmsIffFee: '0.00',
              gsmsMarketingFee: '0.00',
              totalTmsPercent: '0.00',
              targetAnnualUsage: '0',
              contractAnnualRevenue: '0.00',
              gsmsProfit: '0.00',
              contractAnnualGP: '0.00',
              contractAnnualCost: '0.00',
              govSavingsAnalysis: '0.00',
              annualTmopSavings: '0.00',
              monthlyTmopSavings: '0.00',
              tmopPercent: '0.00',
              approvedBy: '',
            };

            // Supplier Bulk NDC dropdown: only from gsms_mfg_ndc from get-bid-details; if null, do not populate
            const gsmsMfgNdcFromApi =
              packData.gsms_mfg_ndc != null && String(packData.gsms_mfg_ndc).trim() !== ''
                ? String(packData.gsms_mfg_ndc).trim()
                : null;
            const finalHasMfgNdc = !!gsmsMfgNdcFromApi;

            packSizeData[tabIndex] = {
              ...defaults,
              ...mappedData,
              selectedMfgNdc: gsmsMfgNdcFromApi ?? undefined,
              mfg_ndc: gsmsMfgNdcFromApi ?? undefined,
              gsms_mfg_ndc: gsmsMfgNdcFromApi ?? undefined,
              packSize: packSize,
              gsmsPackSize: String(packSize),
              mfgPackSize: (() => {
                if (
                  mappedData.mfgPackSize !== null &&
                  mappedData.mfgPackSize !== undefined &&
                  mappedData.mfgPackSize !== ''
                ) {
                  return String(mappedData.mfgPackSize);
                }
                if (packData.mfg_pack_size !== null && packData.mfg_pack_size !== undefined) {
                  return String(packData.mfg_pack_size);
                }
                return String(packSize);
              })(),
              mfgBottleSize: mappedData.mfgBottleSize || packSize,
              _gsmsProfitManuallySet: (() => {
                const hasGsmsProfit =
                  (mappedData.gsmsProfit !== null &&
                    mappedData.gsmsProfit !== undefined &&
                    mappedData.gsmsProfit !== '' &&
                    mappedData.gsmsProfit !== '0.00' &&
                    mappedData.gsmsProfit !== 0) ||
                  (packData.gsms_profit !== null &&
                    packData.gsms_profit !== undefined &&
                    packData.gsms_profit !== '' &&
                    packData.gsms_profit !== '0.00' &&
                    packData.gsms_profit !== 0);
                return hasGsmsProfit;
              })(),
              ndcValidated: finalHasMfgNdc,
            };
          }
        });
      }
    });

    if (isNC) {
      setYearFormData(yearFormDataByTab);
      yearFormDataRef.current = yearFormDataByTab;
    }

    if (Object.keys(packSizeData).length > 0) {
      setPackSizeFormData(packSizeData);

      if (!selectedConcept && formDetails.form_data.length > 0) {
        const firstConcept = formDetails.form_data[0].concept_name;
        setSelectedConcept(firstConcept);
      }

      const firstTabIndex = parseInt(Object.keys(packSizeData)[0]);
      setActivePackSizeTab(firstTabIndex);

      if (isNC) {
        setActiveYearTab(0);
      }

      let initialFormData = packSizeData[firstTabIndex];
      if (isNC) {
        const baseYearData = yearFormDataByTab[firstTabIndex]?.[0] || {};
        initialFormData = { ...initialFormData, ...baseYearData };
      }
      if (apiProductType === 'indirect' && !initialFormData.gsmsHndlgType) {
        initialFormData.gsmsHndlgType = 'Marketing Income';
        setGsmsHndlgType('Marketing Income');
      }

      let parsedAnalysisDate: Date | null = null;
      let apiReturnedAnalysisDate = false;
      const analysisDateFromApi = bidDetails.analystBADate ?? (bidDetails as any).analysis_date;
      if (analysisDateFromApi !== undefined && analysisDateFromApi !== null) {
        apiReturnedAnalysisDate = true;
        if (analysisDateFromApi) {
          try {
            const dateStr = String(analysisDateFromApi).trim();
            if (dateStr !== '') {
              const parts = dateStr.split(/[-/]/);
              if (parts.length === 3) {
                const month = parseInt(parts[0], 10) - 1;
                const day = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
                parsedAnalysisDate = new Date(year, month, day);
              } else {
                parsedAnalysisDate = new Date(dateStr);
              }
              if (isNaN(parsedAnalysisDate.getTime())) {
                parsedAnalysisDate = null;
              }
            }
          } catch (error) {
            console.error('Error parsing analysis date from API:', error);
            parsedAnalysisDate = null;
          }
        }
      }

      let parsedTargetLaunchDate: Date | null = null;
      let apiReturnedTargetLaunchDate = false;
      if (bidDetails.target_launch_date !== undefined && bidDetails.target_launch_date !== null) {
        apiReturnedTargetLaunchDate = true;
        if (bidDetails.target_launch_date) {
          try {
            const dateStr = bidDetails.target_launch_date;
            if (typeof dateStr === 'string' && dateStr.trim() !== '') {
              const parts = dateStr.split(/[-/]/);
              if (parts.length === 3) {
                const month = parseInt(parts[0]) - 1;
                const day = parseInt(parts[1]);
                const year = parseInt(parts[2]);
                parsedTargetLaunchDate = new Date(year, month, day);
              } else {
                parsedTargetLaunchDate = new Date(dateStr);
              }
              if (isNaN(parsedTargetLaunchDate.getTime())) {
                parsedTargetLaunchDate = null;
              }
            }
          } catch (error) {
            console.error('Error parsing target_launch_date:', error);
            parsedTargetLaunchDate = null;
          }
        }
      }

      let finalAnalysisDate: Date | null = null;
      if (apiReturnedAnalysisDate && parsedAnalysisDate) {
        finalAnalysisDate = parsedAnalysisDate;
      } else {
        if (
          initialFormData.analysisDate instanceof Date &&
          !isNaN(initialFormData.analysisDate.getTime())
        ) {
          finalAnalysisDate = initialFormData.analysisDate;
        } else if (initialFormData.analysisDate) {
          try {
            const parsed = new Date(initialFormData.analysisDate);
            if (!isNaN(parsed.getTime())) {
              finalAnalysisDate = parsed;
            }
          } catch (_e) {
            // ignore
          }
        }
      }
      if (!finalAnalysisDate) {
        finalAnalysisDate = new Date();
        finalAnalysisDate.setHours(0, 0, 0, 0);
      }

      let finalTargetLaunchDate: Date | null = null;
      if (apiReturnedTargetLaunchDate) {
        finalTargetLaunchDate = parsedTargetLaunchDate;
      } else {
        finalTargetLaunchDate = initialFormData.targetLaunchDate || null;
      }

      const formDataWithProjectInfo: any = {
        ...initialFormData,
        analysisDate: finalAnalysisDate,
        contracts: initialFormData.contracts || bidDetails.intended_contracts || '',
        targetLaunchDate: finalTargetLaunchDate,
        _gsmsProfitManuallySet: initialFormData._gsmsProfitManuallySet || false,
        ...(initialFormData._gsmsCostFromApi !== undefined && {
          _gsmsCostFromApi: initialFormData._gsmsCostFromApi,
        }),
      };

      if (finalAnalysisDate && !finalTargetLaunchDate && !apiReturnedTargetLaunchDate) {
        try {
          let analysisDateObj: Date;
          if (finalAnalysisDate instanceof Date) {
            analysisDateObj = new Date(finalAnalysisDate.getTime());
          } else if (typeof finalAnalysisDate === 'string') {
            analysisDateObj = new Date(finalAnalysisDate);
          } else {
            analysisDateObj = new Date(finalAnalysisDate);
          }
          if (!isNaN(analysisDateObj.getTime())) {
            const defaultTargetLaunchDate = new Date(analysisDateObj);
            defaultTargetLaunchDate.setMonth(defaultTargetLaunchDate.getMonth() + 6);
            finalTargetLaunchDate = defaultTargetLaunchDate;
            formDataWithProjectInfo.targetLaunchDate = defaultTargetLaunchDate;
          }
        } catch (error) {
          console.warn('Failed to set default target launch date on initial load:', error);
        }
      }

      const hasValidatedNdc =
        formDataWithProjectInfo.gsms_mfg_ndc ||
        formDataWithProjectInfo.mfg_ndc ||
        formDataWithProjectInfo.selectedMfgNdc;
      if (hasValidatedNdc && !formDataWithProjectInfo.selectedMfgNdc) {
        formDataWithProjectInfo.selectedMfgNdc =
          formDataWithProjectInfo.gsms_mfg_ndc || formDataWithProjectInfo.mfg_ndc;
      }
      if (hasValidatedNdc && !formDataWithProjectInfo.mfg_ndc) {
        formDataWithProjectInfo.mfg_ndc =
          formDataWithProjectInfo.gsms_mfg_ndc || formDataWithProjectInfo.selectedMfgNdc;
      }
      if (hasValidatedNdc && !formDataWithProjectInfo.gsms_mfg_ndc) {
        formDataWithProjectInfo.gsms_mfg_ndc =
          formDataWithProjectInfo.mfg_ndc || formDataWithProjectInfo.selectedMfgNdc;
      }

      setFormData(formDataWithProjectInfo);
      const globalFields = [
        'contracts',
        'contractsData',
        'supplierName',
        'analysisDate',
        'targetLaunchDate',
        'analysisType',
        'productName',
        'analystName',
      ];
      const packSizeDataWithoutGlobalFields = Object.keys(formDataWithProjectInfo).reduce(
        (acc: any, key) => {
          if (!globalFields.includes(key)) {
            acc[key] = formDataWithProjectInfo[key];
          }
          return acc;
        },
        {}
      );
      setPackSizeFormData((prev: any) => ({
        ...prev,
        [firstTabIndex]: packSizeDataWithoutGlobalFields,
      }));

      if (initialFormData.conceptName && initialFormData.packSize) {
        const startDate = initialFormData.usagePeriodStartDate;
        const endDate = initialFormData.usagePeriodEndDate;
        if (startDate && endDate) {
          try {
            const parsedStart = parseDateString(startDate);
            const parsedEnd = parseDateString(endDate);
            if (
              parsedStart &&
              parsedEnd &&
              !isNaN(parsedStart.getTime()) &&
              !isNaN(parsedEnd.getTime())
            ) {
              setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
              setCalendarViewDates({
                startViewDate: parsedStart,
                endViewDate: parsedEnd,
              });
            }
          } catch (error) {
            console.error('Error setting chart date range from usage period:', error);
          }
        }
      }

      if (initialFormData.conceptName && initialFormData.packSize && supplierName) {
        loadNdcOptions(
          supplierName,
          initialFormData.conceptName,
          parseFloat(initialFormData.packSize)
        );
      }

      if (initialFormData.ndcValidated === true) {
        setNdcValidated(true);
        if (initialFormData.conceptName && initialFormData.packSize) {
          hasLoadedInitialData.current = false;
        } else {
          console.warn(
            'Cannot load market data: missing conceptName or packSize in initialFormData',
            {
              conceptName: initialFormData.conceptName,
              packSize: initialFormData.packSize,
            }
          );
        }
      } else {
        hasLoadedInitialData.current = false;
      }
    } else {
      if (apiProductType === 'direct' && templateStructure) {
        let parsedAnalysisDate: Date | null = null;
        const analysisDateRaw = bidDetails.analystBADate ?? (bidDetails as any).analysis_date;
        if (analysisDateRaw) {
          try {
            const dateStr = String(analysisDateRaw);
            if (typeof dateStr === 'string') {
              const parts = dateStr.split(/[-/]/);
              if (parts.length === 3) {
                const month = parseInt(parts[0]) - 1;
                const day = parseInt(parts[1]);
                const year = parseInt(parts[2]);
                parsedAnalysisDate = new Date(year, month, day);
              } else {
                parsedAnalysisDate = new Date(dateStr);
              }
              if (isNaN(parsedAnalysisDate.getTime())) {
                parsedAnalysisDate = null;
              }
            }
          } catch (error) {
            console.error('Error parsing analystBADate:', error);
            parsedAnalysisDate = null;
          }
        }

        const defaultData: any = {
          ...templateStructure.cogsBuildUp,
          gsmsHndlgType: '',
          packSize: '',
          gsmsPackSize: '',
          conceptName: '',
          selectedMfgNdc: '',
          mfg_ndc: '',
          gsms_mfg_ndc: '',
          ndcValidated: false,
        };

        const defaultAnalysisDate = (() => {
          const d = parsedAnalysisDate || new Date();
          if (d instanceof Date && !isNaN(d.getTime())) return d;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return today;
        })();
        const globalFieldsData = {
          analysisDate: defaultAnalysisDate,
          contracts: bidDetails.intended_contracts || '',
        };

        setPackSizeFormData({ 0: defaultData });
        setFormData({ ...defaultData, ...globalFieldsData });
        setActivePackSizeTab(0);
      } else {
        let parsedAnalysisDate: Date | null = null;
        const analysisDateRaw = bidDetails.analystBADate ?? (bidDetails as any).analysis_date;
        if (analysisDateRaw) {
          try {
            const dateStr = String(analysisDateRaw);
            if (typeof dateStr === 'string') {
              const parts = dateStr.split(/[-/]/);
              if (parts.length === 3) {
                const month = parseInt(parts[0]) - 1;
                const day = parseInt(parts[1]);
                const year = parseInt(parts[2]);
                parsedAnalysisDate = new Date(year, month, day);
              } else {
                parsedAnalysisDate = new Date(dateStr);
              }
              if (isNaN(parsedAnalysisDate.getTime())) {
                parsedAnalysisDate = null;
              }
            }
          } catch (error) {
            console.error('Error parsing analystBADate:', error);
            parsedAnalysisDate = null;
          }
        }

        const defaultAnalysisDate = (() => {
          const d = parsedAnalysisDate || new Date();
          if (d instanceof Date && !isNaN(d.getTime())) return d;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return today;
        })();
        const emptyFormData: any = {
          analysisDate: defaultAnalysisDate,
          contracts: bidDetails.intended_contracts || '',
        };

        setPackSizeFormData({});
        setFormData(emptyFormData);
      }
    }

    if (templateStructure) {
      setAnalysisType(templateStructure.projectInformation?.analysisType || 'New Launch');
      setGsmsHndlgType(templateStructure.projectInformation?.gsmsHndlgType || '');
      setPricingValidatedChecked(
        templateStructure.projectInformation?.pricingApprovedByPnC || false
      );
    }

    await loadContractsAndHandlingTypes();

    if (
      bidDetails.intended_contracts &&
      typeof bidDetails.intended_contracts === 'string' &&
      bidDetails.intended_contracts.trim()
    ) {
      setFormData((prev: any) => ({
        ...prev,
        contracts: prev.contracts || bidDetails.intended_contracts,
      }));
    }

    if (bidId) {
      await loadSuppliersForBid(parseInt(bidId));
    }
  } catch (error) {
    console.error('Error loading bid details:', error);
  } finally {
    setLoading(false);
  }
}
